import * as React from "react";
import { Form, Formik } from "formik";
import TextField, { TextFieldInput, TextFieldLabel } from "../core/TextField";
import OnChangeComponent from "../utility/OnChangeComponent";
import NumberFormat from "react-number-format";
import { moneyFormatter } from "../../utils";
import { percentOfNum } from "../../utils/numbers";
import Button from "../core/Button";
import { uniq, startCase } from "lodash";
import { Link } from "react-router-dom";

interface IProps {
  config: ICommissionConfig;
  onChange?: (configAttrs: Dictionary) => void;
  readOnly?: boolean;
}

interface IState {}

const cformat = moneyFormatter.format;
const formatPctOf = (pct, num) => cformat(percentOfNum(pct, num));

class CommissionConfigForm extends React.Component<IProps, IState> {
  render() {
    const { config, onChange, readOnly } = this.props;
    return (
      <>
        <Formik
          initialValues={config}
          // allows reinit if initialValues changes
          enableReinitialize={true}
          onSubmit={() => {}}
          render={({ values, setFieldValue, handleChange }) => {
            const disableQuota = quotaName =>
              setFieldValue(
                "disabledQuotas",
                values.disabledQuotas
                  ? uniq([quotaName, ...values.disabledQuotas])
                  : [quotaName]
              );

            const enableQuota = quotaName =>
              setFieldValue(
                "disabledQuotas",
                values.disabledQuotas.filter(q => q !== quotaName)
              );

            const quotaEnabled = values.disabledQuotas
              ? quotaName => !values.disabledQuotas.includes(quotaName)
              : () => true;

            const getSectionStyle = quotaName => ({
              opacity: quotaEnabled(quotaName) ? 1 : 0.5,
            });

            return (
              <Form>
                <OnChangeComponent
                  onChange={onChange}
                  value={values}
                  debounce={3000}
                  propertiesToOmitFromDiff={["updatedAt"]}
                />
                <TextField
                  label={"Name"}
                  name={"name"}
                  onChange={handleChange}
                  value={values.name}
                  readOnly={readOnly}
                  disabled={readOnly}
                />
                <hr />
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <h2>Annuities</h2>
                    </div>
                    <div className="level-item">
                      <DisabledLabel enabled={quotaEnabled("annuities")} />
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <QuotaDisableButton
                        disableQuota={disableQuota}
                        enableQuota={enableQuota}
                        quotaEnabled={quotaEnabled}
                        quotaName={"annuities"}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>
                </div>
                <table className="table" style={getSectionStyle("annuities")}>
                  <thead>
                    <tr>
                      <th>Quota Start</th>
                      <th>Quota End</th>
                      <th>Percent Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {values.annuitiesQuotaThresholds.map((an, i) => (
                      <tr key={i}>
                        <td>
                          <NumberFormat
                            value={an.quotaStart}
                            onValueChange={values =>
                              setFieldValue(
                                `annuitiesQuotaThresholds[${i}].quotaStart`,
                                values.value
                              )
                            }
                            prefix={"$"}
                            thousandSeparator={true}
                            decimalScale={0}
                            allowEmptyFormatting={true}
                            isNumericString={true}
                            customInput={TextFieldInput}
                            readOnly={readOnly || !quotaEnabled("annuities")}
                            disabled={readOnly || !quotaEnabled("annuities")}
                          />
                        </td>
                        <td>
                          <TextFieldInput
                            value={
                              values.annuitiesQuotaThresholds[i + 1]
                                ? cformat(
                                    values.annuitiesQuotaThresholds[i + 1]
                                      .quotaStart - 1
                                  )
                                : cformat(an.quotaStart) + "+"
                            }
                            disabled={true}
                            readOnly={true}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            value={an.percentPaid}
                            onValueChange={values =>
                              setFieldValue(
                                `annuitiesQuotaThresholds[${i}].percentPaid`,
                                values.value
                              )
                            }
                            suffix={"%"}
                            decimalScale={1}
                            fixedDecimalScale={true}
                            allowEmptyFormatting={true}
                            isNumericString={true}
                            customInput={TextFieldInput}
                            readOnly={readOnly || !quotaEnabled("annuities")}
                            disabled={readOnly || !quotaEnabled("annuities")}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <hr />
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <h2>New Agreements</h2>
                    </div>
                    <div className="level-item">
                      <DisabledLabel enabled={quotaEnabled("agreements")} />
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <QuotaDisableButton
                        disableQuota={disableQuota}
                        enableQuota={enableQuota}
                        quotaEnabled={quotaEnabled}
                        quotaName={"agreements"}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>
                </div>
                <div style={getSectionStyle("agreements")}>
                  <TextFieldLabel label={"New Agreements Quota"} />
                  <NumberFormat
                    value={values.agreementsQuota}
                    onValueChange={values =>
                      setFieldValue(`agreementsQuota`, values.value)
                    }
                    prefix={"$"}
                    allowEmptyFormatting={true}
                    isNumericString={true}
                    customInput={TextFieldInput}
                    readOnly={readOnly || !quotaEnabled("agreements")}
                    disabled={readOnly || !quotaEnabled("agreements")}
                  />
                  <TextFieldLabel label={"Max % Paid"} />
                  <NumberFormat
                    value={values.agreementsMaxPercent}
                    onValueChange={values =>
                      setFieldValue(`agreementsMaxPercent`, values.value)
                    }
                    suffix={"%"}
                    allowEmptyFormatting={true}
                    isNumericString={true}
                    customInput={TextFieldInput}
                    readOnly={readOnly || !quotaEnabled("agreements")}
                    disabled={readOnly || !quotaEnabled("agreements")}
                  />
                  <QuotaThresholdsTable
                    array={values.agreementsQuotaThresholds}
                    quota={values.agreementsQuota}
                    field={"agreementsQuotaThresholds"}
                    setFieldValue={setFieldValue}
                    fixedPctPaid={true}
                    readOnly={readOnly || !quotaEnabled("agreements")}
                  />
                </div>
                <hr />
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <h2>Labor</h2>
                    </div>
                    <div className="level-item">
                      <DisabledLabel enabled={quotaEnabled("labor")} />
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <QuotaDisableButton
                        disableQuota={disableQuota}
                        enableQuota={enableQuota}
                        quotaEnabled={quotaEnabled}
                        quotaName={"labor"}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>
                </div>
                <div style={getSectionStyle("labor")}>
                  <TextFieldLabel label={"Labor Quota"} />
                  <NumberFormat
                    value={values.laborQuota}
                    onValueChange={values =>
                      setFieldValue(`laborQuota`, values.value)
                    }
                    prefix={"$"}
                    allowEmptyFormatting={true}
                    isNumericString={true}
                    customInput={TextFieldInput}
                    readOnly={readOnly || !quotaEnabled("labor")}
                    disabled={readOnly || !quotaEnabled("labor")}
                  />
                  <QuotaThresholdsTable
                    array={values.laborQuotaThresholds}
                    quota={values.laborQuota}
                    field={"laborQuotaThresholds"}
                    setFieldValue={setFieldValue}
                    readOnly={readOnly || !quotaEnabled("labor")}
                  />
                </div>
                <hr />
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <h2>Hardware/Software</h2>
                    </div>
                    <div className="level-item">
                      <DisabledLabel
                        enabled={quotaEnabled("hardwareSoftware")}
                      />
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <QuotaDisableButton
                        disableQuota={disableQuota}
                        enableQuota={enableQuota}
                        quotaEnabled={quotaEnabled}
                        quotaName={"hardwareSoftware"}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>
                </div>
                <div style={getSectionStyle("hardwareSoftware")}>
                  <TextFieldLabel label={"Hardware/Software Quota"} />
                  <NumberFormat
                    value={values.hardwareSoftwareQuota}
                    onValueChange={values =>
                      setFieldValue(`hardwareSoftwareQuota`, values.value)
                    }
                    prefix={"$"}
                    allowEmptyFormatting={true}
                    isNumericString={true}
                    customInput={TextFieldInput}
                    readOnly={readOnly || !quotaEnabled("hardwareSoftware")}
                    disabled={readOnly || !quotaEnabled("hardwareSoftware")}
                  />
                  <QuotaThresholdsTable
                    array={values.hardwareQuotaThresholds}
                    quota={values.hardwareSoftwareQuota}
                    field={"hardwareQuotaThresholds"}
                    setFieldValue={setFieldValue}
                    readOnly={readOnly || !quotaEnabled("hardwareSoftware")}
                  />
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "10px 0",
                    margin: "10px 0",
                    borderTop: "1px solid rgb(204, 213, 217)",
                    borderBottom: "1px solid rgb(204, 213, 217)",
                  }}
                >
                  <Link to={"/"}>
                    <Button size="xl" color={"primary"}>
                      Finished
                    </Button>
                  </Link>
                </div>
              </Form>
            );
          }}
        />
      </>
    );
  }
}

export default CommissionConfigForm;

function QuotaThresholdsTable({
  array,
  quota,
  field,
  setFieldValue,
  fixedPctPaid = false,
  readOnly = false,
}) {
  return (
    <table className={"table"}>
      <thead>
        <tr>
          <th>Quota $ Start</th>
          <th>Quota $ End</th>
          <th>Quota % Start</th>
          <th>Quota % End</th>
          <th>Percent Paid</th>
        </tr>
      </thead>
      <tbody>
        {array.map((lq, i, arr) => (
          <tr key={i}>
            <td>
              <QuotaDollarStart current={lq} quota={quota} />
            </td>
            <td>
              <QuotaDollarEnd
                array={arr}
                index={i}
                quota={quota}
                current={lq}
              />
            </td>
            <td>
              <QuotaPctStart
                setFieldValue={setFieldValue}
                readOnly={readOnly}
                current={lq}
                index={i}
                field={field}
              />
            </td>
            <td>
              <QuotaPctEnd current={lq} index={i} array={arr} />
            </td>
            <td>
              {fixedPctPaid ? (
                <TextFieldInput
                  disabled={true}
                  readOnly={true}
                  value={"% of Quota Sold"}
                />
              ) : (
                <PercentPaid
                  setFieldValue={setFieldValue}
                  readOnly={readOnly}
                  current={lq}
                  index={i}
                  field={field}
                />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function QuotaDollarStart({ current, quota }) {
  return (
    <TextFieldInput
      disabled={true}
      readOnly={true}
      value={formatPctOf(current.quotaStart, quota)}
    />
  );
}

function QuotaDollarEnd({ array, index, quota, current }) {
  return (
    <TextFieldInput
      disabled={true}
      readOnly={true}
      value={
        array[index + 1]
          ? cformat(percentOfNum(array[index + 1].quotaStart, quota) - 0.01)
          : formatPctOf(current.quotaStart, quota) + "+"
      }
    />
  );
}

function QuotaPctStart({
  setFieldValue,
  current,
  index,
  field,
  readOnly = false,
}) {
  return (
    <NumberFormat
      value={current.quotaStart}
      onValueChange={values =>
        setFieldValue(`${field}[${index}].quotaStart`, values.value)
      }
      suffix={"%"}
      decimalScale={1}
      fixedDecimalScale={true}
      allowEmptyFormatting={true}
      isNumericString={true}
      customInput={TextFieldInput}
      readOnly={readOnly}
      disabled={readOnly}
    />
  );
}

function QuotaPctEnd({ array, index, current }) {
  return (
    <TextFieldInput
      disabled={true}
      readOnly={true}
      value={
        array[index + 1]
          ? array[index + 1].quotaStart - 0.1 + "%"
          : Number(current.quotaStart) + 0.1 + "%+"
      }
    />
  );
}

function PercentPaid({ setFieldValue, current, field, index, readOnly }) {
  return (
    <NumberFormat
      value={current.percentPaid}
      onValueChange={values =>
        setFieldValue(`${field}[${index}].percentPaid`, values.value)
      }
      suffix={"%"}
      decimalScale={1}
      fixedDecimalScale={true}
      allowEmptyFormatting={true}
      isNumericString={true}
      customInput={TextFieldInput}
      readOnly={readOnly}
      disabled={readOnly}
    />
  );
}

function QuotaDisableButton({
  quotaEnabled,
  disableQuota,
  enableQuota,
  quotaName,
  readOnly = false,
}) {
  if (readOnly) {
    return null;
  }

  const enabled = quotaEnabled(quotaName);
  const humanName = startCase(quotaName);

  const clickAction = enabled
    ? () => disableQuota(quotaName)
    : () => enableQuota(quotaName);

  return (
    <Button
      color={enabled ? "warning" : "info"}
      onClick={clickAction}
      disabled={readOnly}
    >
      {enabled ? `Disable ${humanName} Quota` : `Enable ${humanName} Quota`}
    </Button>
  );
}

function DisabledLabel({ enabled }) {
  if (enabled) {
    return null;
  } else {
    return (
      <span className={"label label-default"} style={{ marginBottom: "5px" }}>
        Disabled
      </span>
    );
  }
}



// WEBPACK FOOTER //
// ./app/javascript/components/commission_calculator/CommissionConfigForm.tsx